import Tool, { BaseTool, type ToolButtonComponent } from './Tool';
import React, { useState, useEffect, useRef } from 'react';
import { ApplicationView, useController } from '@aha-app/mvc';
import ToolbarMenu from '../toolbars/ToolbarMenu';
import ToolButton from '../toolbars/ToolButton';
import WhiteboardController from '../controllers/WhiteboardController';
import { isSimpleGroup, isSimpleSelection } from '../objects/guards';
import { type Instance as TippyInstance } from 'tippy.js/index';
import type EmbeddedTextBehaviors from '../objects/EmbeddedTextBehaviorsV2';
import { AhaEditorUtils } from '@aha-app/aha-editor-v2';

type ObjectWithText = fabric.Object & EmbeddedTextBehaviors;

const canVerticalAlign = (objects: ObjectWithText[]) => {
  return objects.every(object => {
    if (isSimpleGroup(object) || isSimpleSelection(object)) {
      return canVerticalAlign(
        // we check grouped objects before calling this function, so can make this assumption
        object.getObjects() as ObjectWithText[]
      );
    }
    return object.textProps.verticalAlign !== 'none';
  });
};

const forceEndEditing = selection => {
  // If text is being edited, stop editing first so it aligns properly.
  selection.forEach((obj: fabric.Object & EmbeddedTextBehaviors) => {
    const field = obj.activeTextField ? obj.activeTextField : obj;
    // Wireframe components do not have an endTextEditing method
    if (typeof field.endTextEditing === 'function') {
      field.endTextEditing();
    }
  });
};

const AlignButton = (
  tool: typeof ToolTextAlignOption | typeof ToolVerticalAlignOption,
  icon: string
) => {
  const Button: React.FC<{
    currentAlign: string;
    setAlign: (
      tool: typeof ToolTextAlignOption | typeof ToolVerticalAlignOption
    ) => void;
    hidePopup: () => void;
  }> = ApplicationView(({ currentAlign, setAlign, hidePopup }) => (
    <ToolButton
      active={currentAlign === tool.alignValue}
      onClick={() => {
        setAlign(tool);
        hidePopup();
      }}
      tool={tool}
    >
      <aha-icon icon={icon} />
    </ToolButton>
  ));
  return Button;
};

class ToolTextAlignOption extends BaseTool {
  static alignValue: string;

  static action({ controller, selection }) {
    forceEndEditing(selection);

    // Now we set the alignment at the object level so that we can override
    // it based on other settings.
    selection.setOnObjects('textAlign', this.alignValue);

    if (selection.textEditing) {
      selection.applyTextProperty(editor => {
        AhaEditorUtils.Rendering.forceRerender(editor);
      }, true);
    } else {
      controller.canvas.requestRenderAll();
    }
  }
}

class ToolVerticalAlignOption extends BaseTool {
  static alignValue: string;

  static action({ controller, selection }) {
    forceEndEditing(selection);

    if (selection.textEditing) {
      selection.applyTextProperty(editor => {
        AhaEditorUtils.Rendering.forceRerender(editor);
      }, true);
    }

    selection.setOnObjects('verticalAlign', this.alignValue);
    controller.canvas.requestRenderAll();
  }
}

class ToolTextAlignLeft extends ToolTextAlignOption {
  static toolName = 'Align left';
  static alignValue = 'left';

  static buttonComponent() {
    return AlignButton(ToolTextAlignLeft, 'fa-regular fa-align-left');
  }
}

class ToolTextAlignCenter extends ToolTextAlignOption {
  static toolName = 'Align center';
  static alignValue = 'center';

  static buttonComponent() {
    return AlignButton(ToolTextAlignCenter, 'fa-regular fa-align-center');
  }
}

class ToolTextAlignRight extends ToolTextAlignOption {
  static toolName = 'Align right';
  static alignValue = 'right';

  static buttonComponent() {
    return AlignButton(ToolTextAlignRight, 'fa-regular fa-align-right');
  }
}

class ToolVerticalAlignTop extends ToolVerticalAlignOption {
  static toolName = 'Align top';
  static alignValue = 'top';

  static buttonComponent() {
    return AlignButton(ToolVerticalAlignTop, 'fa-regular fa-arrow-up-to-line');
  }
}

class ToolVerticalAlignMiddle extends ToolVerticalAlignOption {
  static toolName = 'Align middle';
  static alignValue = 'middle';

  static buttonComponent() {
    return AlignButton(ToolVerticalAlignMiddle, 'fa-regular fa-arrows-to-line');
  }
}

class ToolVerticalAlignBottom extends ToolVerticalAlignOption {
  static toolName = 'Align bottom';
  static alignValue = 'bottom';

  static buttonComponent() {
    return AlignButton(
      ToolVerticalAlignBottom,
      'fa-regular fa-arrow-down-to-line'
    );
  }
}

const Popup: React.FC<{
  currentAlign: string;
  setCurrentAlign: (tool: typeof ToolTextAlignOption) => void;
  currentVerticalAlign: string;
  setCurrentVerticalAlign: (tool: typeof ToolVerticalAlignOption) => void;
  selection: ObjectWithText;
  hidePopup: () => void;
}> = ApplicationView(
  ({
    currentAlign,
    setCurrentAlign,
    currentVerticalAlign,
    setCurrentVerticalAlign,
    selection,
    hidePopup,
  }) => {
    const controller = useController(WhiteboardController);
    const alignButtons = [
      ToolTextAlignLeft.buttonComponent(),
      ToolTextAlignCenter.buttonComponent(),
      ToolTextAlignRight.buttonComponent(),
    ];

    const verticalAlignButtons = canVerticalAlign([selection])
      ? [
          ToolVerticalAlignTop.buttonComponent(),
          ToolVerticalAlignMiddle.buttonComponent(),
          ToolVerticalAlignBottom.buttonComponent(),
        ]
      : [];

    const setAlign = tool => {
      setCurrentAlign(tool.alignValue);
      controller.actionApplyTool(tool);
    };

    const setVerticalAlign = tool => {
      setCurrentVerticalAlign(tool.alignValue);
      controller.actionApplyTool(tool);
    };

    return (
      <ToolbarMenu columns={3}>
        {alignButtons.map((AlignButton, index) => (
          <AlignButton
            key={index}
            currentAlign={currentAlign}
            setAlign={setAlign}
            hidePopup={hidePopup}
          />
        ))}
        {verticalAlignButtons.map((VerticalAlignButton, index) => (
          <VerticalAlignButton
            key={index}
            currentAlign={currentVerticalAlign}
            setAlign={setVerticalAlign}
            hidePopup={hidePopup}
          />
        ))}
      </ToolbarMenu>
    );
  }
);

const TextAlignButton: ToolButtonComponent<ObjectWithText> = ApplicationView(
  ({ selection, tool }) => {
    const tippyRef = useRef<TippyInstance>();
    const obj = selection.firstObject();
    const field = obj.activeTextField ? obj.activeTextField : obj;
    const textAlign = field.sanitizedTextAlign();
    const verticalAlign = field.sanitizedVerticalAlign();
    const [currentAlign, setCurrentAlign] = useState(textAlign);
    const [currentVerticalAlign, setCurrentVerticalAlign] =
      useState(verticalAlign);

    // Update color if the props change.
    useEffect(() => {
      setCurrentAlign(textAlign);
      setCurrentVerticalAlign(verticalAlign);
    }, [textAlign, verticalAlign]);

    return (
      <ToolButton
        popup={
          <Popup
            currentAlign={currentAlign}
            setCurrentAlign={setCurrentAlign}
            currentVerticalAlign={currentVerticalAlign}
            setCurrentVerticalAlign={setCurrentVerticalAlign}
            selection={selection}
            hidePopup={() => tippyRef.current?.hide()}
          />
        }
        tippyRef={tippyRef}
        tool={tool}
      >
        <aha-icon icon={`fa-regular fa-align-${currentAlign || 'left'}`} />
      </ToolButton>
    );
  }
);

class ToolTextAlign extends Tool {
  static toolName = 'Alignment';

  static buttonComponent() {
    return TextAlignButton;
  }
}

export {
  ToolTextAlign,
  ToolTextAlignLeft,
  ToolTextAlignCenter,
  ToolTextAlignRight,
};
